import { AnalyticsEventName } from '@dupe/common/constants'
import * as Sentry from '@sentry/nextjs'
import { useRouter } from 'next/router'
import { useState } from 'react'
import OnboardingTitle from '~/src/components/onboarding/OnboardingTitle'
import { useAuthContext } from '~/src/context/AuthProvider'
import { useAuthUser } from '~/src/hooks'
import useAnalytics from '~/src/hooks/useAnalytics'
import useUpdateUserMetadata from '~/src/hooks/useUpdateUserMetadata'
import logger from '~/src/lib/logger'
import {
  useOnboardingActions,
  useOnboardingData,
} from '~/src/stores/useOnboardingStore'

interface Props {
  closeModal?: () => void
}

const CompleteStep: React.FC<Props> = ({ closeModal }) => {
  const analytics = useAnalytics()
  const router = useRouter()
  const { authUser } = useAuthUser()
  const { handleAuthSuccess, refetchUserData } = useAuthContext()

  const { setIsProfessional, addTagToBeehiiv } = useOnboardingActions()

  const { mutateAsync: updateUserMetadata } = useUpdateUserMetadata()

  const onboardingData = useOnboardingData()

  const [isProfessional, setIsProfessionalState] = useState<string | undefined>(
    onboardingData?.isProfessional || undefined
  )

  const handleFinish = async (value: string) => {
    analytics.track(AnalyticsEventName.AuthModalOnboardingSubmit, {
      step: 'complete',
      isProfessional: value,
    })

    // Save to store
    setIsProfessional(value)

    // Save to database
    const updateMetadataResp = await updateUserMetadata({
      key: 'onboarding_data',
      value: {
        ...onboardingData,
        isProfessional: value,
      },
    })

    const updatedMetadata = updateMetadataResp.user.metadata

    // Add Beehiiv tag for isProfessional if possible
    const subscriptionId = updatedMetadata?.beehiiv_subscription_id
    const isProfessional =
      value || updatedMetadata?.onboarding_data?.isProfessional

    if (authUser && subscriptionId && isProfessional === 'yes') {
      try {
        await addTagToBeehiiv({
          authUser: {
            uid: authUser.uid,
            accessToken: authUser.accessToken,
            getIdToken: authUser.getIdToken?.bind(authUser),
          },
          tag: 'isprofessional',
          subscriptionId,
          refetchUserData,
        })
      } catch (error) {
        Sentry.captureException(error)
        logger.error('Failed to add Beehiiv "isprofessional" tag:', { error })
      }
    }

    // This is when we trigger any auth callbacks
    // TODO - breakout these callback into onboarding complete callback and auth callback
    handleAuthSuccess(closeModal)

    // Remove onboarding params from URL using Next.js router
    if (
      router.query.onboarding ||
      router.query.status ||
      router.query.session_id
    ) {
      const newQuery = { ...router.query }
      delete newQuery.onboarding
      delete newQuery.status
      delete newQuery.session_id

      router.replace(
        {
          pathname:
            router.pathname === '/[hash]'
              ? `/results/${router.pathname}`
              : router.pathname,
          query: newQuery,
        },
        undefined,
        { shallow: true }
      )
    }
  }

  // Handle button click
  const handleButtonClick = (value: string) => {
    setIsProfessionalState(value)
    handleFinish(value)
  }

  return (
    <div className='space-y-6'>
      <OnboardingTitle
        title={
          <div className='space-x-2'>
            <span className='text-3xl'>🎉</span>
            <span>Thank you for signing up!</span>
          </div>
        }
        description='One last question before we get started.'
        disableBack
      />

      <div className='space-y-4 rounded-lg bg-gray-100 p-4 text-black'>
        <div className='font-medium'>
          Are you a trade professional or interior designer?
        </div>
        <div className='flex items-center justify-evenly gap-2'>
          <button
            onClick={() => handleButtonClick('yes')}
            className={`h-16 w-full rounded-md transition-colors ${
              isProfessional === 'yes'
                ? 'bg-black text-white'
                : 'bg-white hover:bg-gray-50'
            }`}
            type='button'
          >
            Yes
          </button>
          <button
            onClick={() => handleButtonClick('no')}
            className={`h-16 w-full rounded-md transition-colors ${
              isProfessional === 'no'
                ? 'bg-black text-white'
                : 'bg-white hover:bg-gray-50'
            }`}
            type='button'
          >
            No
          </button>
        </div>
      </div>
    </div>
  )
}

export default CompleteStep
